(() => {
  if (window.FTRPreselectedBundle) return;

  const GROUP_SELECTOR = "[data-preselected-bundle]";
  const selectionStates = new WeakMap();
  const selectionLocks = new WeakSet();
  const requestLocks = new WeakSet();
  const controlDisabledStates = new WeakMap();

  function getSelectedLoopSellingPlanId() {
    const value = typeof window.FTRLoopBundle?.getSelectedSellingPlanId === "function"
      ? window.FTRLoopBundle.getSelectedSellingPlanId()
      : window.FTRLoopBundle?.selectedSellingPlanId;
    if (value == null || value === "") return null;
    const numericId = Number(String(value).split("/").pop());
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  function logFinalCartPayload(items, selectedSellingPlanId) {
    console.log("[FTR Loop Bundle] Selected selling plan before cart:", selectedSellingPlanId);
    console.log("[FTR Loop Bundle] Final Loop-style payload:", { items });
  }

  function getItems(group) {
    return Array.from(group.querySelectorAll(".bundle-item"));
  }

  function setItemSelected(item, isSelected) {
    item.classList.toggle("selected", isSelected);
    item.classList.remove("active", "is-selected");
    item.setAttribute("aria-selected", String(isSelected));

    const button = item.querySelector(".select-btn");
    if (button) {
      button.classList.toggle("is-selected", isSelected);
      button.classList.remove("active");
      button.setAttribute("aria-pressed", String(isSelected));
    }

    const group = item.closest(GROUP_SELECTOR);
    const variantId = item.dataset.variantId;
    const selectedProducts = group ? selectionStates.get(group) : null;
    if (selectedProducts && variantId) {
      if (isSelected) selectedProducts.set(variantId, item);
      else selectedProducts.delete(variantId);
    }
  }

  function initializeGroup(group) {
    if (!group || group.dataset.initialized === "true") return;

    const grid = group.querySelector(".bundle-grid");
    if (!grid) return;

    group.dataset.initialized = "true";
    selectionStates.set(group, new Map());
    const limit = Number.parseInt(grid.dataset.limit || "0", 10);

    getItems(group).forEach((item, index) => {
      setItemSelected(item, index < limit);
      const button = item.querySelector(".select-btn");
      if (button) button.disabled = item.dataset.available === "false";
    });
  }

  function initializeBundles(root = document) {
    if (root.matches?.(GROUP_SELECTOR)) initializeGroup(root);
    root.querySelectorAll?.(GROUP_SELECTOR).forEach(initializeGroup);
  }

  function getProductInfo(element) {
    return element.closest(".product-info")
      || element.closest(".container")?.querySelector(".product-info")
      || null;
  }

  function getGroups(productInfo) {
    return Array.from(productInfo?.querySelectorAll(GROUP_SELECTOR) || []);
  }

  function showError(productInfo, message) {
    const group = getGroups(productInfo)[0];
    if (!group) return;

    let error = productInfo.querySelector(".bundle-add-error");
    if (!error) {
      error = document.createElement("div");
      error.className = "bundle-add-error";
      error.setAttribute("role", "alert");
      error.setAttribute("aria-live", "polite");
      group.insertAdjacentElement("afterend", error);
    }

    error.textContent = message;
    error.hidden = false;
  }

  function clearError(productInfo) {
    const error = productInfo?.querySelector(".bundle-add-error");
    if (error) {
      error.textContent = "";
      error.hidden = true;
    }
  }

  function ensureLoadingMarkup(control) {
    if (!control || control.querySelector(".bundle-add-button__loader")) return;

    const text = document.createElement("span");
    text.className = "bundle-add-button__text";
    while (control.firstChild) text.appendChild(control.firstChild);

    const loader = document.createElement("span");
    loader.className = "bundle-add-button__loader";
    loader.hidden = true;
    loader.setAttribute("aria-hidden", "true");

    control.append(text, loader);
  }

  function setControlLoading(control, isLoading) {
    if (!control) return;
    ensureLoadingMarkup(control);

    if (isLoading) controlDisabledStates.set(control, Boolean(control.disabled));

    control.classList.toggle("is-bundle-loading", isLoading);
    control.setAttribute("aria-busy", String(isLoading));
    if ("disabled" in control) {
      control.disabled = isLoading || Boolean(controlDisabledStates.get(control));
    }
    control.setAttribute(
      "aria-disabled",
      String(isLoading || Boolean(controlDisabledStates.get(control))),
    );

    const text = control.querySelector(".bundle-add-button__text");
    const loader = control.querySelector(".bundle-add-button__loader");
    if (text) text.hidden = isLoading;
    if (loader) loader.hidden = !isLoading;
    if (!isLoading) controlDisabledStates.delete(control);
  }

  function getAddControls(productInfo) {
    const controls = [];
    const main = productInfo?.querySelector(
      '.js-product-form-main .product-info__add-button [type="submit"]',
    );
    const sticky = productInfo?.closest(".container")?.querySelector(
      ".card--sticky-atc .card__buttons a",
    );
    if (main) controls.push(main);
    if (sticky) controls.push(sticky);
    return controls;
  }

  function resetBundleSelection(productInfo) {
    getGroups(productInfo).forEach((group) => {
      selectionStates.get(group)?.clear();
      getItems(group).forEach((item) => {
        setItemSelected(item, false);

        const selectButton = item.querySelector(".select-btn");
        if (selectButton) {
          selectButton.disabled = item.dataset.available === "false";
        }

        item.querySelectorAll(
          '[data-bundle-quantity], .qty-input, input[name*="quantity"]',
        ).forEach((input) => {
          input.value = "0";
        });
      });

      group.querySelectorAll("[data-bundle-count]").forEach((counter) => {
        counter.textContent = "0";
      });
    });
  }

  function buildItems(productInfo, parentProductId) {
    const setId = `set-${Date.now()}`;
    const uniqueItems = new Map();

    getGroups(productInfo).forEach((group) => {
      const selectedProducts = selectionStates.get(group) || new Map();
      selectedProducts.forEach((item) => {
        if (item.dataset.available === "false") return;

        const variantId = Number(item.dataset.variantId);
        if (!Number.isInteger(variantId) || variantId <= 0 || uniqueItems.has(String(variantId))) return;

        uniqueItems.set(String(variantId), {
          id: variantId,
          quantity: 1,
          properties: {
            _set: setId,
            _parent_product_id: parentProductId,
            _bundle_group: item.closest(".bundle-grid")?.dataset.id || "unified",
          },
        });
      });
    });

    if (uniqueItems.size === 0) return [];

    const parentItem = {
      id: Number(parentProductId),
      quantity: 1,
      properties: { _parent: true, _set: setId },
    };

    const productForm = productInfo.querySelector(".js-product-form-main");
    const sellingPlanValue = productForm
      ?.querySelector('[name="selling_plan"]')
      ?.value?.trim();
    const sellingPlanId = sellingPlanValue ? Number(sellingPlanValue) : null;
    if (Number.isInteger(sellingPlanId) && sellingPlanId > 0) {
      parentItem.selling_plan = sellingPlanId;
    }

    return [...uniqueItems.values(), parentItem];
  }

  async function handleAddToCart(event, trigger) {
    const productInfo = getProductInfo(trigger);
    if (!productInfo || getGroups(productInfo).length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (requestLocks.has(productInfo)) return;

    const parentProductId = productInfo.querySelector(
      ".js-product-form-main input[name='id']",
    )?.value;
    if (!parentProductId) {
      showError(productInfo, "Parent product ID not found.");
      return;
    }

    let items = buildItems(productInfo, parentProductId);
    if (items.length === 0) {
      showError(productInfo, "Please select at least one bundle product.");
      return;
    }

    requestLocks.add(productInfo);
    clearError(productInfo);
    const controls = getAddControls(productInfo);
    controls.forEach((control) => setControlLoading(control, true));
    let succeeded = false;

    try {
      window.FTRLoopBundle?.syncSelectedPlanFromWidget?.(productInfo);
      const isLoopSubscription = window.FTRLoopBundle?.purchaseType === "subscription";
      if (isLoopSubscription && window.FTRLoopBundle?.prepareCartItems) {
        items = await window.FTRLoopBundle.prepareCartItems(items, productInfo);
      } else {
        // Preserve the original parent/child Cart Transform payload for one-time bundles.
        items = items.map((item) => window.FTRLoopBundle?.removeLoopPropertiesFromOneTimeItem
          ? window.FTRLoopBundle.removeLoopPropertiesFromOneTimeItem(item)
          : { ...item, properties: { ...(item.properties || {}) } });
        items.forEach((item) => delete item.selling_plan);
      }

      const selectedSellingPlanId = getSelectedLoopSellingPlanId();
      if (isLoopSubscription && !selectedSellingPlanId) {
        throw new Error("Please select a valid subscription frequency.");
      }
      if (!Array.isArray(items) || items.length === 0) {
        console.error("[FTR Loop Bundle] Empty cart items before Shopify request.", { items });
        throw new Error("The bundle cart payload is empty.");
      }
      logFinalCartPayload(items, selectedSellingPlanId);
      const shopifyCartPayload = { items };
      console.log("[FTR Loop Bundle] Stage 6 Shopify payload:", shopifyCartPayload);
      console.log("[FTR Loop Bundle] Final /cart/add.js payload:", shopifyCartPayload);
      console.log("[FTR Loop Bundle] Final Shopify add request:", shopifyCartPayload);

      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(shopifyCartPayload),
      });

      const cartRawText = await response.text();
      let result;
      try {
        result = cartRawText ? JSON.parse(cartRawText) : null;
      } catch (error) {
        console.error("[FTR Loop Bundle] Invalid Shopify cart JSON:", { cartRawText, error });
        throw new Error("Shopify returned an invalid cart response.");
      }
      console.log("[FTR Loop Bundle] Shopify cart response:", { status: response.status, payload: result });
      if (!response.ok) {
        if (window.FTRLoopBundle?.selectedSellingPlanId) {
          console.error("[FTR Loop Bundle] Shopify rejected the Loop-style subscription payload.", {
            sellingPlanShopifyId: selectedSellingPlanId,
            items,
            response: result,
          });
          throw new Error("The selected subscription option is unavailable for one or more bundle items.");
        }
        throw new Error(result.description || result.message || "Unable to add products to cart.");
      }
      if (isLoopSubscription && window.FTRLoopBundle?.validateAddedBundleLines) {
        await window.FTRLoopBundle.validateAddedBundleLines(items);
      }

      resetBundleSelection(productInfo);
      succeeded = true;
      if (succeeded && window.FTRCart && typeof window.FTRCart.open === "function") {
        await window.FTRCart.open();
      } else {
        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open", { bubbles: true }));
      }
    } catch (error) {
      console.error("[FTR Loop Bundle] Bundle add-to-cart error:", error);
      showError(productInfo, error.customerMessage || error.message || "Something went wrong while adding to cart.");
    } finally {
      controls.forEach((control) => setControlLoading(control, false));
      requestLocks.delete(productInfo);
    }

    
  }

  function handleBundleClick(event) {
    const selectButton = event.target.closest(".select-btn");
    if (selectButton) {
      const item = selectButton.closest(".bundle-item");
      const group = selectButton.closest(GROUP_SELECTOR);
      if (!item || !group || selectButton.disabled || selectionLocks.has(selectButton)) return;

      event.preventDefault();
      event.stopPropagation();
      selectionLocks.add(selectButton);
      selectButton.disabled = true;

      const grid = group.querySelector(".bundle-grid");
      const limit = Number.parseInt(grid?.dataset.limit || "0", 10);
      const selected = getItems(group).filter((card) => card.classList.contains("selected"));
      const isSelected = item.classList.contains("selected");
      const duplicate = selected.some(
        (card) => card !== item && card.dataset.variantId === item.dataset.variantId,
      );

      if (!isSelected && duplicate) {
        selectionLocks.delete(selectButton);
        selectButton.disabled = false;
        return;
      }

      if (!isSelected && selected.length >= limit) {
        alert(`You can only select ${limit} item(s) in this bundle.`);
      } else {
        setItemSelected(item, !isSelected);
      }

      window.setTimeout(() => {
        selectionLocks.delete(selectButton);
        selectButton.disabled = item.dataset.available === "false";
      }, 200);
      return;
    }

    const item = event.target.closest(`${GROUP_SELECTOR} .bundle-item`);
    if (!item) return;

    const group = item.closest(GROUP_SELECTOR);
    const siblingPopup = group?.nextElementSibling;
    const popup = siblingPopup?.matches(".bundle-popup")
      ? siblingPopup
      : document.querySelector("#bundle-desc-popup");
    if (!popup) return;

    popup.querySelector("#bundle-popup-title").textContent = item.dataset.title || "";
    popup.querySelector("#bundle-popup-text").innerHTML = item.dataset.desc || "No description available.";
    popup.setAttribute("aria-hidden", "false");
  }

  function handleDocumentClick(event) {
    if (event.target.closest(".select-btn") || event.target.closest(`${GROUP_SELECTOR} .bundle-item`)) {
      handleBundleClick(event);
      return;
    }

    const close = event.target.closest(".bundle-popup-close");
    const overlay = event.target.classList.contains("bundle-popup-overlay");
    if (close || overlay) {
      event.target.closest(".bundle-popup")?.setAttribute("aria-hidden", "true");
      return;
    }

    const addControl = event.target.closest(
      '.js-product-form-main .product-info__add-button [type="submit"], .card--sticky-atc .card__buttons a',
    );
    if (addControl) handleAddToCart(event, addControl);
  }

  document.addEventListener("click", handleDocumentClick);
  document.addEventListener("submit", (event) => {
    const form = event.target.closest(".js-product-form-main");
    if (form) handleAddToCart(event, form.querySelector('[type="submit"]') || form);
  }, true);
  document.addEventListener("shopify:section:load", (event) => initializeBundles(event.target));

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeBundles());
  } else {
    initializeBundles();
  }

  window.FTRPreselectedBundle = {
    initialize: initializeBundles,
    reset: resetBundleSelection,
  };
})();
